import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import {
  fetchBasePractice,
  fetchBillingAddress,
  fetchContactInfo,
  postContactInfo,
  updatePracticeInfoAddress,
} from "./practiceInformation"
import {
  fetchLiabilityInsurances,
  putLiabilityInsurance,
} from "./liabilityInsurance"
import { getAuthAndReleasesData } from "./authAndReleases"
import { createContactInfoPayload } from "./helpers"
import { Practice } from "../schema"
import { getTaxInfoData } from "./taxInformation"
import {
  getLocationsThunk,
  getVirtualOfficeInformationThunk,
} from "./officeInformation"
import { getPracticeAgreementData } from "./practiceAgreement"
import { updateSelectedLocation } from "../model"
import { DataStatus } from "../../enums"
import { getPracticeCompletenessData } from "./practiceCompleteness"
import { getPracticeAffiliates } from "./practiceAffiliates"

interface PracticeDataStatus {
  authAndReleasesDataStatus: DataStatus
  authAndReleasesSigningRequestDataStatus: DataStatus
  fullPracticeDataStatus: DataStatus
  basePracticeDataStatus: DataStatus
  postOrSelectPracticeDataStatus: DataStatus
  practiceAddressDataStatus: DataStatus
  practiceAffiliatesDataStatus: DataStatus
  practiceCompletenessDataStatus: DataStatus
  practiceContactDataStatus: DataStatus
  practiceDetailsDataStatus: DataStatus
  practiceTagsDataStatus: DataStatus
  liabilityInformationDataStatus: DataStatus
  taxTablesDataStatus: DataStatus
  taxInfoGetDataStatus: DataStatus
  taxInfoPostDataStatus: DataStatus
  taxInfoPutDataStatus: DataStatus
  taxInfoDeleteDataStatus: DataStatus
  locationPhotosGetDataStatus: DataStatus
  locationPhotoGetDataStatus: DataStatus
  locationPhotoPostDataStatus: DataStatus
  locationPhotoDeleteDataStatus: DataStatus
  locationsGetDataStatus: DataStatus
  locationPostPutDataStatus: DataStatus
  locationDeleteDataStatus: DataStatus
  virtualLocationGetDataStatus: DataStatus
  virtualLocationDeleteDataStatus: DataStatus
  virtualLocationPutDataStatus: DataStatus
  practiceAgreementGetDataStatus: DataStatus
  practiceAgreementSigningRequestDataStatus: DataStatus
  knoxKeeneSigningRequestDataStatus: DataStatus
  knoxKeeneDeleteDataStatus: DataStatus
  // US 20047 - AR "Re-sign and Replace"
  practiceAgreementResetDataStatus: DataStatus
  knoxKeeneResetDataStatus: DataStatus
}

const { LOADING, LOADED, SENDING, ERROR } = DataStatus
const initialDataStatuses: PracticeDataStatus = {
  authAndReleasesDataStatus: LOADING,
  authAndReleasesSigningRequestDataStatus: LOADING,
  fullPracticeDataStatus: LOADING,
  basePracticeDataStatus: LOADING,
  postOrSelectPracticeDataStatus: LOADING,
  practiceAddressDataStatus: LOADING,
  practiceAffiliatesDataStatus: LOADING,
  practiceCompletenessDataStatus: LOADING,
  practiceContactDataStatus: LOADING,
  practiceDetailsDataStatus: LOADING,
  practiceTagsDataStatus: LOADING,
  liabilityInformationDataStatus: LOADING,
  taxTablesDataStatus: LOADING,
  taxInfoGetDataStatus: LOADING,
  taxInfoPostDataStatus: LOADING,
  taxInfoDeleteDataStatus: LOADING,
  taxInfoPutDataStatus: LOADING,
  locationPhotosGetDataStatus: LOADING,
  locationPhotoGetDataStatus: LOADING,
  locationPhotoPostDataStatus: LOADING,
  locationPhotoDeleteDataStatus: LOADING,
  locationsGetDataStatus: LOADING,
  locationPostPutDataStatus: LOADING,
  locationDeleteDataStatus: LOADING,
  virtualLocationGetDataStatus: LOADING,
  virtualLocationDeleteDataStatus: LOADING,
  virtualLocationPutDataStatus: LOADING,
  practiceAgreementGetDataStatus: LOADING,
  practiceAgreementSigningRequestDataStatus: LOADING,
  knoxKeeneSigningRequestDataStatus: LOADING,
  knoxKeeneDeleteDataStatus: LOADING,
  practiceAgreementResetDataStatus: LOADING,
  knoxKeeneResetDataStatus: LOADING,
}

interface PracticeLoaderState {
  dataStatuses: PracticeDataStatus
}

const intitialLoaderState: PracticeLoaderState = {
  dataStatuses: initialDataStatuses,
}

export const practiceLoaderSlice = createSlice({
  name: "practiceLoader",
  initialState: intitialLoaderState,
  reducers: {
    practiceDataStatusUpdated: (
      state: PracticeLoaderState,
      { payload }: PayloadAction<Partial<PracticeDataStatus>>,
    ) => {
      state.dataStatuses = {
        ...state.dataStatuses,
        ...payload,
      }
    },
  },
})

export default practiceLoaderSlice.reducer

// Export actions and reducer
export const { practiceDataStatusUpdated } = practiceLoaderSlice.actions

export const selectPracticeDataStatus = (state: {
  practiceLoader: PracticeLoaderState
}): PracticeDataStatus => {
  return state.practiceLoader.dataStatuses
}

export const selectPracticeIsLoadingOrSending = (state: {
  practiceLoader: PracticeLoaderState
}): boolean => {
  return Object.values(state.practiceLoader.dataStatuses).some(
    (status) => status === LOADING || status === SENDING,
  )
}

export interface PracticeName {
  id: string
  name: string
}

export interface MainPracticeThunkArgs {
  userId: string
  practiceId: string
  setDataStatus?: (dataStatus: DataStatus) => void
}
export const mainPracticeThunk = createAsyncThunk(
  "practiceProfile/getAllPracticeInfo",
  async (
    { userId, practiceId, setDataStatus }: MainPracticeThunkArgs,
    { dispatch },
  ) => {
    if (setDataStatus) {
      setDataStatus(SENDING)
    } else {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          fullPracticeDataStatus: SENDING,
        }),
      )
    }

    const partialPractice = await dispatch(
      fetchBasePractice({ userId: userId, practiceId: practiceId }),
    )
    if (!!partialPractice.payload) {
      await dispatch(fetchBillingAddress(practiceId))
      await dispatch(fetchContactInfo(practiceId))
      await dispatch(fetchLiabilityInsurances(practiceId))

      // make API call to update details - PLACEHOLDER (waiting for logo image url)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          practiceDetailsDataStatus: LOADED,
        }),
      )

      // TODO: replace these with the something similar to above
      await dispatch(getTaxInfoData({ practiceId }))
      await dispatch(getAuthAndReleasesData(practiceId))
      await dispatch(getVirtualOfficeInformationThunk(practiceId))
      // reset the selected location on any full load
      await dispatch(updateSelectedLocation(null))
      await dispatch(getLocationsThunk(practiceId))
      await dispatch(getPracticeAffiliates(practiceId))
      await dispatch(getPracticeAgreementData({ practiceId }))

      await dispatch(getPracticeCompletenessData(practiceId))

      if (setDataStatus) {
        setDataStatus(LOADED)
      } else {
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            fullPracticeDataStatus: LOADED,
          }),
        )
      }
    } else {
      if (setDataStatus) {
        setDataStatus(ERROR)
      } else {
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            fullPracticeDataStatus: ERROR,
          }),
        )
      }
    }
  },
)

export interface updatePracticeArgs {
  userId: string
  practiceId: string
  payload: Partial<Practice>
  setDataStatuses?: {
    setPatchPracticeDataStatus?: (status: DataStatus) => void
    setPutPracticeBillingAddressDataStatus?: (status: DataStatus) => void
    setPostPracticeContactInfoDataStatus?: (status: DataStatus) => void
  }
}
/**
 * This function is intended to be the main function to call for updating practice information, as it will handle calling the relevant API calls based on the payload provided.
 * Currently, it handles updating base practice information, practice address, and practice contact information, but it can be expanded to include other fields as needed.
 *
 * WARNING: This interface includes the Partial type for Practice, which means that it can accept any field within the Practice schema, but not all fields will be updated by this function.
 * It is the responsibility of the caller to ensure that the payload provided is valid and includes only the fields that are intended to be updated.
 */
export const updatePractice = createAsyncThunk(
  "practiceProfile/updatePractice",
  async (
    { userId, practiceId, payload, setDataStatuses }: updatePracticeArgs,
    { dispatch },
  ) => {
    // TODO - add the other needed update fields/update calls (upcoming refactor)
    const { setPostPracticeContactInfoDataStatus } = setDataStatuses || {}

    const callPatchBasePractice =
      !!payload.name ||
      payload.name === "" ||
      !!payload.websiteUrl ||
      payload.websiteUrl === "" ||
      !!payload.overview ||
      typeof payload.isVisible === "boolean"
    const callPutBillingAddress = !!payload.practiceInfo?.practiceAddress

    // handles patch base practice and put billing address together since they have page overlaps
    if (callPatchBasePractice || callPutBillingAddress) {
      await dispatch(
        updatePracticeInfoAddress({ practiceId, payload, setDataStatuses }),
      )
    }

    // make API call to update contact
    if (!!payload.practiceInfo?.practiceContact) {
      const practiceContact = payload.practiceInfo.practiceContact
      const contactInfo = createContactInfoPayload(practiceContact)
      await dispatch(
        postContactInfo({
          practiceId,
          userId,
          payload: contactInfo,
          setDataStatus: setPostPracticeContactInfoDataStatus,
        }),
      )
    }

    if (!!payload.liabilityInsurances) {
      await dispatch(
        putLiabilityInsurance({
          practiceId,
          documentId: payload.liabilityInsurances[0].id,
          dateUpdates: {
            issueDate: payload.liabilityInsurances[0].issueDate,
            expirationDate: payload.liabilityInsurances[0].expirationDate,
          },
        }),
      )
    }
  },
)
